export const SORT_DIRECTION = {
    asc: 'asc',
    desc: 'desc'
} as const;

export type SortDirection = (typeof SORT_DIRECTION)[keyof typeof SORT_DIRECTION];

export type SortColumnsMap = Record<string, string>;

export type SortColumnValue<TSortColumns extends SortColumnsMap> =
    TSortColumns[keyof TSortColumns];

export type SortSelectionObject<TSortColumns extends SortColumnsMap> = {
    column: SortColumnValue<TSortColumns>;
    direction: SortDirection;
};

export type SortSelectionString<TSortColumns extends SortColumnsMap> =
    `${SortColumnValue<TSortColumns>}:${SortDirection}`;

export function toSortSelectionString<TSortColumns extends SortColumnsMap>(
    selection: SortSelectionObject<TSortColumns>,
): SortSelectionString<TSortColumns> {
    return `${selection.column}:${selection.direction}` as SortSelectionString<TSortColumns>;
}

export function fromSortSelectionString<TSortColumns extends SortColumnsMap>(
    sortColumns: TSortColumns,
    serialized: string | null | undefined,
): SortSelectionObject<TSortColumns> | null {
    if (!serialized) return null;

    const separatorIndex = serialized.lastIndexOf(':');
    if (separatorIndex <= 0) return null;

    const rawColumn = serialized.slice(0, separatorIndex);
    const rawDirection = serialized.slice(separatorIndex + 1);

    if (!isSortDirection(rawDirection)) return null;
    if (!isSortColumn(sortColumns, rawColumn)) return null;

    return {
        column: rawColumn,
        direction: rawDirection,
    } as SortSelectionObject<TSortColumns>;
}

function isSortDirection(value: string): value is SortDirection {
    return value === SORT_DIRECTION.asc || value === SORT_DIRECTION.desc;
}

function isSortColumn<TSortColumns extends SortColumnsMap>(
    sortColumns: TSortColumns,
    value: string,
): value is SortColumnValue<TSortColumns> {
    return (Object.values(sortColumns) as string[]).includes(value);
}
